/**
 * SVG data URL cache to avoid redundant conversions
 */
const svgDataUrlCache = {};

/**
 * Fetch SVG file and convert to data URL
 * This solves CORS issues with S3 SVG files
 */
export const convertSvgToDataUrl = async (svgUrl) => {
  if (!svgUrl || !svgUrl.includes('.svg')) {
    return null;
  }

  // Check cache first
  if (svgDataUrlCache[svgUrl]) {
    return svgDataUrlCache[svgUrl];
  }

  try {
    const response = await fetch(svgUrl, {
      headers: {
        'Accept': 'image/svg+xml,image/*;q=0.8,*/*;q=0.5',
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch SVG: ${response.statusText}`);
    }

    const blob = await response.blob();
    const dataUrl = URL.createObjectURL(blob);

    // Cache the data URL
    svgDataUrlCache[svgUrl] = dataUrl;

    return dataUrl;
  } catch (error) {
    console.error('Error converting SVG to data URL:', svgUrl, error);
    return null;
  }
};

/**
 * Get the appropriate image URL - converts SVG to data URL for CORS compatibility
 */
export const getImageUrl = async (imageUrl) => {
  if (!imageUrl) return null;

  // Check if it's an SVG file
  if (imageUrl.includes('.svg')) {
    return await convertSvgToDataUrl(imageUrl);
  }

  // For non-SVG images, return as-is
  return imageUrl;
};

/**
 * Preload image to verify it loads before displaying
 */
export const preloadImage = (imageUrl) => {
  return new Promise((resolve) => {
    if (!imageUrl) {
      resolve(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      resolve(imageUrl);
    };

    img.onerror = () => {
      console.warn('Failed to load image:', imageUrl);
      resolve(null);
    };

    img.src = imageUrl;
  });
};

/**
 * Cleanup blob URLs (call this when component unmounts)
 */
export const cleanupBlobUrls = () => {
  Object.values(svgDataUrlCache).forEach((dataUrl) => {
    try {
      URL.revokeObjectURL(dataUrl);
    } catch (error) {
      console.error('Error revoking object URL:', error);
    }
  });
};

export default getImageUrl;

